"use client";
import React, { useState } from "react";
import { Box, HStack, IconButton, VStack, Stack } from "@chakra-ui/react";
import {
  MdArrowForwardIos,
  MdArrowBackIos,
} from "@onemind-services-llc/react-icons-ng/md";
import Image from "next/image";
import Link from "next/link";
import { Carousel } from "@onemind-services-llc/ui-components";
import { useCustomerCarousel } from "@/utils/useCustomerCarousel";
import { MAIN_ROUTES } from "@/constants/routes";
import PageLayout from "@/layouts/pageLayout";
import { Heading, Text, Button } from "@/elements";
import SecAwardsLogo from "public/home/secAwardsLogo.png";
import CmlLogo from "public/shared/cmlLogo.png";
import BgImage from "public/home/achievementsBg.png";
import { ACHIEVEMENTS_LIST } from "./constants";

type Props = { title: string; desc: string };

const AchievementCard = (props: Props) => {
  const { title, desc } = props;
  return (
    <VStack gap={6} p={{ md: 4 }}>
      <Image src={SecAwardsLogo} alt="Achievement Logo" width={200} height={150} />
      <Stack gap={1} color="white">
        <Heading as="div" variant={"headLine3"} textAlign="center">
          OneMindServices LLC - CloudMyLab wins{" "}
          <Box
            as="span"
            fontWeight="600"
            whiteSpace={{ base: "normal", md: "nowrap" }}
            display="inline"
          >
            {title}
          </Box>
        </Heading>
        <Text variant={"label1"} textAlign={"center"}>
          - The Security Awards 2025
        </Text>
      </Stack>
      <Box
        p={6}
        mx={{ md: 8 }}
        border="1px solid black"
        maxW="6xl"
        fontSize={"lg"}
        borderRadius={8}
        bg="white"
      >
        <Text>{desc}</Text>
        <Text fontWeight={600} mt={2}>
          - Annabella Whittall
        </Text>
        <Text fontStyle={"italic"}>COO , The Cloud Awards</Text>
      </Box>
      <Button
        variant={"secondary"}
        as={Link}
        href={`${MAIN_ROUTES.ABOUT_US}?section=sec-achievements`}
      >
        Learn More
      </Button>
    </VStack>
  );
};

const AchievementComp = () => {
  const [carouselActions, setCarouselActions] = useState({
    slickPrev: () => {},
    slickNext: () => {},
  });

  const { slickPrev, slickNext } = carouselActions;
  const { settings } = useCustomerCarousel({
    dots: true,
    autoplay: false,
    slidesToShow: 1,
  });

  return (
    <Box
      bgImage={`url(${BgImage.src})`}
      bgSize="cover"
      backgroundPosition="center"
      backgroundRepeat="no-repeat"
      py={10}
      position="relative"
    >
      <PageLayout marginTop={0}>
        <Box position="relative">
          <Carousel settings={settings} setSliderRef={setCarouselActions}>
            {ACHIEVEMENTS_LIST.map((achievement, index) => (
              <AchievementCard key={index} {...achievement} />
            ))}
          </Carousel>

          {/* Desktop Arrows */}
          <HStack
            gap={6}
            display={{ base: "none", md: "flex" }}
            position="absolute"
            top="50%"
            left="0"
            right="0"
            justifyContent="space-between"
            px={4}
            zIndex="2"
          >
            <IconButton
              aria-label="previous"
              color="white"
              fontSize="30px"
              onClick={slickPrev}
              variant="ghost"
              _hover={{ bg: "transparent" }}
            >
              <MdArrowBackIos />
            </IconButton>
            <IconButton
              aria-label="next"
              color="white"
              fontSize="30px"
              onClick={slickNext}
              variant="ghost"
              _hover={{ bg: "transparent" }}
            >
              <MdArrowForwardIos />
            </IconButton>
          </HStack>

          {/* Mobile Arrows */}
          <HStack
            gap={6}
            display={{ base: "flex", md: "none" }}
            justifyContent="center"
            mt={6}
          >
            <IconButton
              aria-label="previous"
              color="white"
              fontSize="24px"
              onClick={slickPrev}
              variant="ghost"
              _hover={{ bg: "transparent" }}
            >
              <MdArrowBackIos />
            </IconButton>
            <IconButton
              aria-label="next"
              color="white"
              fontSize="24px"
              onClick={slickNext}
              variant="ghost"
              _hover={{ bg: "transparent" }}
            >
              <MdArrowForwardIos />
            </IconButton>
          </HStack>
        </Box>
      </PageLayout>

      {/* Badge Positioning */}
      <Box
        position={{ base: "static", md: "absolute" }}
        bottom="10px"
        right="10px"
        bg="white"
        px={3}
        py={2}
        borderRadius="md"
        boxShadow="md"
        w="fit-content"
        zIndex={9999}
        mt={{ base: 6, md: 0 }}
        textAlign={{ base: "center", md: "left" }}
        mx={{ base: "auto", md: "0" }}
      >
        <Box
          asChild
          display="flex"
          alignItems="flex-end"
          gap={1}
          style={{
            color: "#1E87F0",
            fontWeight: 600,
            marginLeft: "4px",
            fontSize: "18px",
          }}
        >
          <Link href="https://cloudmylab.com" target="_blank" rel="noopener noreferrer">
            Explore
            <Image
              src={CmlLogo}
              alt="CloudMyLab"
              width={100}
              height={100}
              style={{ display: "inline-block", verticalAlign: "middle" }}
              loading="lazy"
            />
          </Link>
        </Box>
      </Box>
    </Box>
  );
};

export default AchievementComp;
